/** Cloudinary automatic quality level, from the lightest file to the sharpest. */
export type ImageQuality = "low" | "eco" | "good" | "best";

type ImageTransform = {
	width: number;
	height?: number;
	quality?: number | "auto" | `auto:${ImageQuality}`;
	crop?: "limit" | "fill";
	blur?: number;
};

export const isCloudinary = (src: string): boolean => src.startsWith("https://res.cloudinary.com/");

const isSvg = (src: string): boolean => /\.svg(\?|$)/i.test(src);

/** Cloudinary raster image the transforms apply to. An SVG stays as is: `f_auto` would rasterise it. */
export const isOptimizable = (src: string): boolean => isCloudinary(src) && !isSvg(src);

/** Adds a transform segment to a Cloudinary URL: appended to a `t_pfs` preset, else inserted after `/upload/`. */
const withTransforms = (src: string, transforms: string): string => {
	if (src.includes("t_pfs")) {
		return src.replace("t_pfs", `t_pfs,${transforms}`);
	}
	return src.replace("/upload/", `/upload/${transforms}/`);
};

export const buildImageUrl = (
	src: string,
	{ width, height, quality = "auto", crop, blur }: ImageTransform
): string => {
	if (!isOptimizable(src)) {
		return src;
	}
	const transforms = [`w_${width}`];
	if (height) {
		transforms.push(`h_${height}`);
	}
	transforms.push(`c_${crop ?? (height ? "fill" : "limit")}`, `q_${quality}`, "f_auto");
	if (blur) {
		transforms.push(`e_blur:${blur}`);
	}
	return withTransforms(src, transforms.join(","));
};

/** srcset for the given candidate widths — each keeps the width/height ratio (`w` descriptors). */
export const buildSrcSet = (
	src: string,
	{ width, height }: { width: number; height: number },
	targetWidths: number[],
	quality: ImageQuality
): string =>
	targetWidths
		.map(targetWidth => {
			const targetHeight = Math.round(targetWidth * (height / width));
			const url = buildImageUrl(src, {
				width: targetWidth,
				height: targetHeight,
				quality: `auto:${quality}`,
			});
			return `${url} ${targetWidth}w`;
		})
		.join(", ");

export const buildLqip = (src: string): string => buildImageUrl(src, { width: 32, quality: 1 });
